import { useEffect, useMemo, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import {
  Plus,
  Beaker,
  HardHat,
  SprayCan,
  Package,
  ArrowDownToLine,
  ArrowUpFromLine,
  AlertTriangle,
  Clock,
  Send,
  Download,
  Tag,
  QrCode,
  Printer,
  Ruler,
  Layers,
  Search,
} from "lucide-react";
import { API, api, getToken, getImpersonationToken } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import StockLabel, { printStockLabel, printLabelSheet } from "@/components/StockLabel";
import { Checkbox } from "@/components/ui/checkbox";
import { ProductForm } from "@/components/stock/ProductForm";
import { LotEntryDialog } from "@/components/stock/LotEntryDialog";
import { StockOutDialog } from "@/components/stock/StockOutDialog";
import { ProductTable } from "@/components/stock/ProductTable";
import { fmtDate, fmtQty } from "@/components/stock/stockUtils";

const BUILTIN_ICON = { chemical: Beaker, ppe: HardHat, cleaning: SprayCan, other: Package };
const LABEL_PRESETS = [{ w: 40, h: 25 }, { w: 50, h: 30 }, { w: 60, h: 40 }, { w: 80, h: 50 }, { w: 100, h: 60 }];
const MANAGE_ROLES = ["super_admin", "company_admin", "manager"];

export default function Sanitary() {
  const { user } = useAuth();
  const canManage = MANAGE_ROLES.includes(user?.role);
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [alerts, setAlerts] = useState({ low_stock: [], expiring: [] });
  const [categories, setCategories] = useState([]);
  const [showEmpty, setShowEmpty] = useState(false);
  const [search, setSearch] = useState("");
  // dialogs
  const [productFormOpen, setProductFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [entryProduct, setEntryProduct] = useState(null);
  const [outProduct, setOutProduct] = useState(null);
  const [openCat, setOpenCat] = useState(false);
  const [catForm, setCatForm] = useState({ key: "", label: "" });
  const [sendingWa, setSendingWa] = useState(false);
  // etiqueta QR
  const [openLabel, setOpenLabel] = useState(null); // lot
  const [labelCfg, setLabelCfg] = useState({ label_width_mm: 60, label_height_mm: 40 });
  const [openLabelCfg, setOpenLabelCfg] = useState(false);
  const [cfgForm, setCfgForm] = useState({ label_width_mm: 60, label_height_mm: 40 });
  const labelRef = useRef(null);
  // impressão em lote
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchSel, setBatchSel] = useState([]);
  const [batchCopies, setBatchCopies] = useState(1);
  const batchRef = useRef(null);

  const load = async () => {
    try {
      const [p, m, a, c, s] = await Promise.all([
        api.get(`/sanitary/products?include_empty=${showEmpty}`),
        api.get("/sanitary/movements"),
        api.get("/sanitary/alerts"),
        api.get("/sanitary/categories"),
        api.get("/sanitary/settings").catch(() => ({ data: { label_width_mm: 60, label_height_mm: 40 } })),
      ]);
      setProducts(p.data);
      setMovements(m.data);
      setAlerts(a.data);
      setCategories(c.data);
      setLabelCfg(s.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEmpty]);

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.key, c])), [categories]);
  const catInfo = (key) => {
    const c = catMap[key];
    return c ? { label: c.label, tone: c.tone || "bg-stone-200 text-stone-700" } : { label: key || "—", tone: "bg-stone-200 text-stone-700" };
  };
  const catIcon = (key) => BUILTIN_ICON[key] || Tag;

  const allLots = useMemo(() => products.flatMap((p) => p.lots || []), [products]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => [p.name, p.brand, p.sku, p.location].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [products, search]);

  const stats = useMemo(
    () => ({
      products: products.length,
      lots: products.reduce((s, p) => s + (p.lots_count || 0), 0),
      low: alerts.low_stock_count || 0,
      expiring: alerts.expiring_count || 0,
    }),
    [products, alerts]
  );

  const removeProduct = async (p) => {
    if (!window.confirm(`Excluir o produto "${p.name}" e todos os seus ${p.lots_total} lote(s)?`)) return;
    try {
      await api.delete(`/sanitary/products/${p.id}`);
      toast.success("Produto removido");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    }
  };

  const saveLabelCfg = async () => {
    try {
      const { data } = await api.put("/sanitary/settings", {
        label_width_mm: Number(cfgForm.label_width_mm),
        label_height_mm: Number(cfgForm.label_height_mm),
      });
      setLabelCfg(data);
      setOpenLabelCfg(false);
      toast.success("Tamanho da etiqueta salvo");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar tamanho");
    }
  };

  const saveCategory = async () => {
    const key = catForm.key.trim().toLowerCase().replace(/\s+/g, "_");
    if (!key || !catForm.label.trim()) {
      toast.error("Key e Nome são obrigatórios");
      return;
    }
    try {
      await api.post("/sanitary/categories", { key, label: catForm.label.trim() });
      toast.success("Categoria criada");
      setCatForm({ key: "", label: "" });
      setOpenCat(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    }
  };

  const authedDownload = async (path, filename) => {
    const token = getImpersonationToken() || getToken();
    const resp = await fetch(`${API}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
    if (!resp.ok) throw new Error(await resp.text());
    const url = URL.createObjectURL(await resp.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportLabelPdf = async (lot) => {
    try {
      await authedDownload(`/sanitary/items/${lot.id}/label.pdf`, `etiqueta-${(lot.name || "produto").replace(/[^\w\s-]/g, "").trim()}.pdf`);
      toast.success("Etiqueta exportada em PDF");
    } catch {
      toast.error("Falha ao exportar etiqueta");
    }
  };

  const exportLowStockCsv = async () => {
    try {
      await authedDownload("/sanitary/low-stock/export.csv", "estoque-baixo.csv");
      toast.success("CSV baixado");
    } catch (err) {
      toast.error(err?.message || "Falha no export");
    }
  };

  const sendWhatsapp = async () => {
    setSendingWa(true);
    try {
      const { data } = await api.post("/sanitary/low-stock/whatsapp");
      if (data.sent) toast.success(`Relatório enviado para ${data.phone}`);
      else if (data.reason === "no_low_stock") toast.success("Nenhum produto em estoque baixo — nada a enviar!");
      else toast.error(data.error || "Falha ao enviar");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar");
    } finally {
      setSendingWa(false);
    }
  };

  return (
    <Layout
      title="Estoque"
      subtitle="Produtos com lotes, validade e baixa PVPS (primeiro que vence, primeiro que sai)"
      actions={
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="border-stone-300 bg-white" onClick={() => { setBatchSel([]); setBatchCopies(1); setBatchOpen(true); }} data-testid="batch-print-btn">
            <Printer className="h-4 w-4 mr-2" /> Imprimir em lote
          </Button>
          <Button variant="outline" className="border-stone-300 bg-white" onClick={() => { setCfgForm({ ...labelCfg }); setOpenLabelCfg(true); }} data-testid="stock-label-settings">
            <Ruler className="h-4 w-4 mr-2" /> Etiqueta
          </Button>
          <Dialog open={openCat} onOpenChange={setOpenCat}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-stone-300 bg-white" data-testid="stock-new-category">
                <Tag className="h-4 w-4 mr-2" /> Nova categoria
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display">Nova categoria de estoque</DialogTitle>
                <DialogDescription className="font-body text-xs text-stone-500">Personalize as categorias para melhor organizar seu inventário.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Nome exibido *</Label>
                  <Input value={catForm.label} onChange={(e) => setCatForm({ ...catForm, label: e.target.value })} className="mt-1 bg-white" placeholder="Ex: Utensílios" data-testid="stock-cat-label" autoFocus />
                </div>
                <div>
                  <Label>Chave interna *</Label>
                  <Input value={catForm.key} onChange={(e) => setCatForm({ ...catForm, key: e.target.value.toLowerCase().replace(/\s+/g, "_") })} className="mt-1 bg-white font-mono text-sm" placeholder="Ex: utensilios" data-testid="stock-cat-key" />
                  <p className="text-[11px] text-stone-500 mt-1 font-body">Identificador único, sem espaços (auto-normalizado).</p>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={saveCategory} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="stock-cat-submit">Criar categoria</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {canManage && (
            <Button className="bg-emerald-800 hover:bg-emerald-900 text-white" onClick={() => { setEditingProduct(null); setProductFormOpen(true); }} data-testid="sanitary-new">
              <Plus className="h-4 w-4 mr-2" /> Novo produto
            </Button>
          )}
        </div>
      }
    >
      <div data-testid="sanitary-page" className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Produtos", value: stats.products, icon: Package, tone: "text-stone-700 bg-stone-100" },
            { label: "Lotes ativos", value: stats.lots, icon: Layers, tone: "text-emerald-700 bg-emerald-100" },
            { label: "Estoque baixo", value: stats.low, icon: AlertTriangle, tone: "text-rose-700 bg-rose-100" },
            { label: "Lotes vencendo em 30d", value: stats.expiring, icon: Clock, tone: "text-amber-700 bg-amber-100" },
          ].map((k) => (
            <Card key={k.label} className="p-5 rounded-xl border-stone-200 bg-white">
              <div className={`h-9 w-9 rounded-md flex items-center justify-center ${k.tone}`}><k.icon className="h-4 w-4" /></div>
              <div className="mt-3 font-display text-3xl font-extrabold tracking-tight">{k.value}</div>
              <div className="text-xs text-stone-500 font-body uppercase tracking-wider mt-1">{k.label}</div>
            </Card>
          ))}
        </div>

        {(alerts.low_stock?.length > 0 || alerts.expiring?.length > 0) && (
          <Card className="p-5 rounded-xl border-amber-200 bg-amber-50/60">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-md bg-amber-200/70 text-amber-900 flex items-center justify-center shrink-0"><AlertTriangle className="h-4 w-4" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <h3 className="font-display font-bold tracking-tight">Atenção</h3>
                  {alerts.low_stock?.length > 0 && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="border-amber-300 bg-white text-amber-900 hover:bg-amber-100" onClick={exportLowStockCsv} data-testid="stock-low-export">
                        <Download className="h-3.5 w-3.5 mr-1.5" /> Exportar CSV
                      </Button>
                      <Button size="sm" onClick={sendWhatsapp} disabled={sendingWa} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="stock-low-whatsapp">
                        <Send className="h-3.5 w-3.5 mr-1.5" /> {sendingWa ? "Enviando…" : "Enviar para WhatsApp"}
                      </Button>
                    </div>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {alerts.low_stock?.slice(0, 8).map((p) => (
                    <Badge key={p.id} className="bg-rose-100 text-rose-800 border-none">Baixo · {p.name} ({fmtQty(p.quantity)} {p.unit})</Badge>
                  ))}
                  {alerts.expiring?.slice(0, 8).map((lot) => (
                    <Badge key={lot.id} className="bg-amber-200/80 text-amber-900 border-none">
                      {lot._expires_in_days < 0 ? "Vencido" : `Vence em ${lot._expires_in_days}d`} · {lot.name}{lot.batch ? ` (lote ${lot.batch})` : ""}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        <Card className="rounded-xl border-stone-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-display font-bold tracking-tight">Produtos</h3>
              <p className="text-xs text-stone-500 font-body">Clique no produto para ver os lotes. Entrada cria lote; saída segue PVPS.</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar produto…" className="pl-8 h-8 w-52 bg-white" data-testid="product-search" />
              </div>
              <label className="flex items-center gap-1.5 text-xs font-body text-stone-600 cursor-pointer">
                <Checkbox checked={showEmpty} onCheckedChange={(v) => setShowEmpty(!!v)} data-testid="show-empty-lots" /> mostrar lotes zerados
              </label>
            </div>
          </div>
          <ProductTable
            products={filtered}
            catInfo={catInfo}
            catIcon={catIcon}
            canManage={canManage}
            onEntry={setEntryProduct}
            onOut={setOutProduct}
            onLabel={setOpenLabel}
            onEdit={(p) => { setEditingProduct(p); setProductFormOpen(true); }}
            onDelete={removeProduct}
            onReload={load}
          />
        </Card>

        <Card className="rounded-xl border-stone-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-200">
            <h3 className="font-display font-bold tracking-tight">Últimos movimentos</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Produto</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Qtde</TableHead>
                <TableHead>Motivo / lote</TableHead>
                <TableHead>Por</TableHead>
                <TableHead>Quando</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {movements.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-stone-500 py-10">Sem movimentações</TableCell></TableRow>
              )}
              {movements.slice(0, 30).map((m) => (
                <TableRow key={m.id} data-testid={`sanitary-mv-${m.id}`}>
                  <TableCell className="font-display font-semibold">{m.item_name}</TableCell>
                  <TableCell>
                    <Badge className={`border-none ${m.type === "in" ? "bg-emerald-100 text-emerald-800" : m.type === "out" ? "bg-rose-100 text-rose-800" : "bg-stone-200 text-stone-700"}`}>
                      {m.type === "in" ? <ArrowDownToLine className="h-3 w-3 mr-1" /> : m.type === "out" ? <ArrowUpFromLine className="h-3 w-3 mr-1" /> : null}
                      {m.type === "in" ? "Entrada" : m.type === "out" ? "Saída" : "Ajuste"}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-body tabular-nums">{fmtQty(m.quantity)}</TableCell>
                  <TableCell className="font-body text-sm text-stone-700 whitespace-normal break-words max-w-xs">{m.reason || "—"}</TableCell>
                  <TableCell className="font-body text-xs">{m.user_name || "—"}</TableCell>
                  <TableCell className="font-body text-xs text-stone-500">{formatDateTime(m.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <ProductForm
          open={productFormOpen}
          onOpenChange={(o) => { setProductFormOpen(o); if (!o) setEditingProduct(null); }}
          product={editingProduct}
          categories={categories}
          userName={user?.name}
          onSaved={(_, lot) => { load(); if (lot) setOpenLabel(lot); }}
        />
        <LotEntryDialog product={entryProduct} userName={user?.name} onOpenChange={() => setEntryProduct(null)} onCreated={(lot) => { load(); setOpenLabel(lot); }} />
        <StockOutDialog product={outProduct} onOpenChange={() => setOutProduct(null)} onDone={load} />

        {/* Etiqueta QR do LOTE */}
        <Dialog open={!!openLabel} onOpenChange={(o) => !o && setOpenLabel(null)}>
          <DialogContent className="max-w-2xl" data-testid="stock-label-dialog">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2">
                <QrCode className="h-5 w-5 text-emerald-800" /> Etiqueta — {openLabel?.name}
                {openLabel?.batch && <Badge className="bg-stone-100 text-stone-700 border-none">lote {openLabel.batch}</Badge>}
              </DialogTitle>
              <DialogDescription className="font-body text-xs text-stone-500">
                Etiqueta {labelCfg.label_width_mm}×{labelCfg.label_height_mm} mm com QR Code que identifica este lote no sistema. A impressão sai no tamanho exato configurado.
              </DialogDescription>
            </DialogHeader>
            <div ref={labelRef} className="flex justify-center py-3 overflow-auto bg-stone-100 rounded-lg">
              {openLabel && <StockLabel item={openLabel} widthMm={Number(labelCfg.label_width_mm) || 60} heightMm={Number(labelCfg.label_height_mm) || 40} />}
            </div>
            {openLabel && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-body text-stone-600">
                <div>Produção: <b>{fmtDate(openLabel.production_date)}</b></div>
                <div>Validade: <b>{fmtDate(openLabel.expires_at)}</b></div>
                <div>Lote: <b>{fmtQty(openLabel.lot_quantity ?? openLabel.quantity)} {openLabel.unit}</b></div>
                <div>Responsável: <b>{openLabel.responsible || "—"}</b></div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" className="border-stone-300 bg-white" onClick={() => { setCfgForm({ ...labelCfg }); setOpenLabelCfg(true); }} data-testid="stock-label-size-btn">
                <Ruler className="h-4 w-4 mr-2" /> Tamanho
              </Button>
              <Button variant="outline" className="border-stone-300 bg-white" onClick={() => exportLabelPdf(openLabel)} data-testid="stock-label-export-pdf">
                <Download className="h-4 w-4 mr-2" /> Exportar PDF
              </Button>
              <Button className="bg-emerald-800 hover:bg-emerald-900 text-white" onClick={() => printStockLabel(labelRef.current, Number(labelCfg.label_width_mm) || 60, Number(labelCfg.label_height_mm) || 40)} data-testid="stock-label-print">
                <Printer className="h-4 w-4 mr-2" /> Imprimir etiqueta
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Tamanho da etiqueta (por empresa) */}
        <Dialog open={openLabelCfg} onOpenChange={setOpenLabelCfg}>
          <DialogContent className="max-w-sm" data-testid="stock-label-config-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">Tamanho da etiqueta</DialogTitle>
              <DialogDescription className="font-body text-xs text-stone-500">Configure o tamanho para impressão direta. Vale para toda a empresa.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex flex-wrap gap-1.5">
                {LABEL_PRESETS.map((pz) => {
                  const active = Number(cfgForm.label_width_mm) === pz.w && Number(cfgForm.label_height_mm) === pz.h;
                  return (
                    <Button key={`${pz.w}x${pz.h}`} type="button" size="sm" variant="outline" className={active ? "bg-emerald-800 text-white border-emerald-800 hover:bg-emerald-900 hover:text-white" : "bg-white border-stone-300"} onClick={() => setCfgForm({ label_width_mm: pz.w, label_height_mm: pz.h })} data-testid={`label-preset-${pz.w}x${pz.h}`}>
                      {pz.w}×{pz.h} mm
                    </Button>
                  );
                })}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Largura (mm)</Label>
                  <Input type="number" min="20" max="210" value={cfgForm.label_width_mm} onChange={(e) => setCfgForm({ ...cfgForm, label_width_mm: e.target.value })} className="mt-1 bg-white" data-testid="label-width-input" />
                </div>
                <div>
                  <Label>Altura (mm)</Label>
                  <Input type="number" min="15" max="297" value={cfgForm.label_height_mm} onChange={(e) => setCfgForm({ ...cfgForm, label_height_mm: e.target.value })} className="mt-1 bg-white" data-testid="label-height-input" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={saveLabelCfg} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="label-config-save">Salvar tamanho</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Impressão de etiquetas em lote (A4) — por LOTE */}
        <Dialog open={batchOpen} onOpenChange={setBatchOpen}>
          <DialogContent className="max-w-md" data-testid="batch-print-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">Imprimir etiquetas em lote</DialogTitle>
              <DialogDescription className="font-body text-xs text-stone-500">
                Selecione os lotes. As etiquetas saem em folha A4 no tamanho configurado ({labelCfg.label_width_mm}×{labelCfg.label_height_mm} mm).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="max-h-64 overflow-y-auto divide-y divide-stone-100 border border-stone-200 rounded-md">
                {allLots.map((lot) => (
                  <label key={lot.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-stone-50" data-testid={`batch-item-${lot.id}`}>
                    <Checkbox checked={batchSel.includes(lot.id)} onCheckedChange={(v) => setBatchSel((s) => (v ? [...s, lot.id] : s.filter((x) => x !== lot.id)))} />
                    <span className="flex-1 min-w-0">
                      <span className="font-body text-sm font-semibold block truncate">{lot.name}{lot.batch ? ` · lote ${lot.batch}` : ""}</span>
                      <span className="text-[11px] text-stone-500 font-body">Val: {fmtDate(lot.expires_at)} · {fmtQty(lot.quantity)} {lot.unit}</span>
                    </span>
                  </label>
                ))}
                {allLots.length === 0 && <div className="px-3 py-4 text-xs text-stone-400 font-body">Nenhum lote com saldo.</div>}
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Cópias por lote</Label>
                <Input type="number" min="1" max="50" value={batchCopies} onChange={(e) => setBatchCopies(e.target.value)} className="w-20 bg-white" data-testid="batch-copies-input" />
              </div>
            </div>
            <DialogFooter>
              <Button disabled={batchSel.length === 0} onClick={() => printLabelSheet(batchRef.current)} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="batch-print-confirm">
                <Printer className="h-4 w-4 mr-2" /> Imprimir {batchSel.length * (Number(batchCopies) || 1) || ""} etiqueta(s)
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="fixed -left-[9999px] top-0" aria-hidden="true">
          <div ref={batchRef}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4mm" }}>
              {batchSel.flatMap((id) => {
                const lot = allLots.find((x) => x.id === id);
                if (!lot) return [];
                return Array.from({ length: Math.max(Number(batchCopies) || 1, 1) }).map((_, i) => (
                  <StockLabel key={`${id}-${i}`} item={lot} widthMm={Number(labelCfg.label_width_mm) || 60} heightMm={Number(labelCfg.label_height_mm) || 40} />
                ));
              })}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
